import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What the six boards that had no detail panel put in one, away from any rendering.
 *
 * `ExceptionsBoard` has had `ExceptionDetail` since it was written, and `ActiveDeliveries` and the
 * live map each show the selected row. The other six — active rides, the demand queue, the driver
 * roster, the offer monitor, service areas and geofences — drew a table and nothing else, so a row
 * could be read across but never opened. The design draws a detail panel on every one of them, and
 * `CLAUDE.md` names row detail panels among the things a build may not drop.
 *
 * ## The rule every field here follows
 *
 * **A field appears when the operation behind the board published it, and is absent otherwise.** No
 * row is enriched by a second request: the panel shows what the board already holds, which is why
 * opening a row cannot fail, cannot be slow, and cannot answer 403 on a screen that just drew the
 * row. Where the design's panel names something the service does not publish, the field carries the
 * absence and says which service would have to publish it — the treatment the deliveries board's
 * `Brand` column and the customers board's `Phone` already use.
 *
 * That is also why these builders take the row types the boards already use rather than the
 * services' own: the panel is a second reading of a row on screen, not a second source for it.
 */

/** One line of the panel's field table. */
export interface DetailField {
  readonly name: string;
  readonly value: string;
  /** Geist Mono, for an identifier, a code or a timer — the design's own rule. */
  readonly mono?: boolean;
  /**
   * A field the board's operation does not publish, drawn as the design's `—` in `--text3`.
   *
   * Never a blank and never a guess. `title` carries which service would have to publish it, so the
   * absence is answerable rather than merely visible.
   */
  readonly absent?: boolean;
  readonly title?: string;
}

/** Everything the panel draws for one row. */
export interface RecordDetail {
  /** The row's own name, or its identifier where the service publishes no name. */
  readonly headline: string;
  /** The identifier, always shown and always monospaced, because it is what a report is filed on. */
  readonly identifier: string;
  /** The service codes behind this row, in the mono chip the design puts under the headline. */
  readonly source: string;
  /**
   * The badge at the head of the panel: the same state the row shows, never a second reading.
   *
   * `code` and `status` travel with it because `StateBadge` takes the owning service and the
   * contract's own word, and refuses to draw a badge for a status its table does not cover rather
   * than inventing a grey one. Carrying them here means the panel's badge is the row's badge —
   * computed once, by the board, from the same fields.
   */
  readonly state?: {
    readonly classKey: StateClassKey;
    readonly label: string;
    readonly code: string;
    readonly status: string;
  };
  readonly fields: readonly DetailField[];
  /** The sentence under the fields, where this board has one worth repeating. */
  readonly note?: string;
}

/** A present field. */
export function field(name: string, value: string, mono = false): DetailField {
  return mono ? { name, value, mono: true } : { name, value };
}

/**
 * A field this board cannot fill, with the reason attached.
 *
 * Takes the reason rather than defaulting one, because "not published" is not a reason — the
 * useful sentence names the service and what it would have to publish, which is what turns a blank
 * on a screen into an entry somebody can act on.
 */
export function absent(name: string, why: string): DetailField {
  return { name, value: "—", absent: true, title: why };
}

/**
 * A field that is present only when the value is.
 *
 * `undefined` and the empty string both mean the operation answered without it, and both drop the
 * line rather than drawing an empty one — which is different from `absent`, where the *contract*
 * has no such field at all. A panel that drew both the same way would say this platform cannot
 * publish a value when the truth is that this row does not have one.
 */
export function optional(
  name: string,
  value: string | undefined | null,
  mono = false,
): readonly DetailField[] {
  return value === undefined || value === null || value === "" ? [] : [field(name, value, mono)];
}

/**
 * An instant, as the console writes one.
 *
 * The board's own clock formats the relative ages; this is the absolute one, because a panel is
 * where somebody reads the actual time to put in a message to somebody else.
 */
export function at(instant: string | undefined | null): string | undefined {
  // Both, because a contract publishes `null` for an instant that has not happened and a row the
  // board shaped itself may simply not carry the key. Neither is a time, and both drop the line.
  if (instant === undefined || instant === null) return undefined;
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return instant;
  return new Date(parsed).toISOString().replace("T", " ").replace(".000Z", " UTC");
}

/**
 * A coordinate pair, at the precision a person can act on.
 *
 * Five decimal places is about a metre, and more digits on a screen read as more certainty than a
 * phone's fix has. The accuracy, where the service publishes one, is a separate field rather than
 * being folded in here.
 */
export function coordinate(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}
